import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { User as IFBUser } from '@angular/fire/auth';

import { CustomErrorCodes } from 'src/custom_error_codes.constants';
import { from, of, switchMap, tap } from 'rxjs';
import { captureException } from '@sentry/angular';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './services/auth.service';
import { UserService } from './services/user.service';
import { RealtimeService } from './services/realtime.service';
import { RealtimeSpaceService } from './services/realtime-space.service';
import { FlagsService, FLAGS } from './services/flags.service';
import { IUserInfo, User } from './models/user';
import { NotSupportedBrowsersComponent } from './dialogs/not-supported-browsers/not-supported-browsers.component';
import { ProfilesService } from './services/profiles.service';
import { ModalManagerService } from './services/modal-manager.service';
import { UserInfoInputDialogService } from './services/user-info-input-dialog.service';
import { redirectToAppScript } from './common/utils/common-util';
import { AccountEntryService } from './account/account-entry/account-entry.service';
import { ERRORS } from './common/utils/notification-constants';
import { NotificationToasterService } from './services/notification-toaster.service';
import { AuthTracing } from './utilities/auth-tracing.utils';
import { FeatureManagementService } from './services/feature-management.service';
import { InspectModeRepository } from './state/inspect-mode-repository.service';
import { RecordingDetailsService } from './services/recording-details.service';
import { AnalyticsSessionsDetailsService } from './analytics-sessions/analytics-sessions-details.service';

@UntilDestroy()
@Injectable()
export class LoginActivate implements CanActivate, CanLoad {
  readonly LOGIN_PATH = 'account/login';

  isUploading = false;
  user: User | null = null;
  firebaseUser?: IFBUser | null;
  redirectDetected = false;

  constructor(
    private authService: AuthService,
    private router: Router,
    private userService: UserService,
    private translate: TranslateService,
    private realtimeService: RealtimeService,
    private realtimeSpaceService: RealtimeSpaceService,
    private flagsService: FlagsService,
    private deviceService: DeviceDetectorService,
    private profilesService: ProfilesService,
    private modalManagerService: ModalManagerService,
    private userInfoInputDialogService: UserInfoInputDialogService,
    private accountEntryService: AccountEntryService,
    private notificationToasterService: NotificationToasterService,
    private featureManagementService: FeatureManagementService,
    private inspectModeRepository: InspectModeRepository,
    private recordingDetailsService: RecordingDetailsService,
    private analyticsSessionsDetailsService: AnalyticsSessionsDetailsService,
  ) {
    this.userService.isUploading.pipe(untilDestroyed(this)).subscribe((res) => {
      this.isUploading = res;
    });

    this.userService.user.pipe(untilDestroyed(this)).subscribe((res) => {
      this.user = res && res.user;
    });

    this.authService.firebaseUser.pipe(untilDestroyed(this)).subscribe((res) => {
      this.firebaseUser = res;
    });
  }

  async loadTutor(route?: Route, segments?: UrlSegment[]): Promise<boolean> {
    this.userService.appLoading.next(true);
    return new Promise<boolean>((resolve, reject) => {
      this.userService
        .getUser()
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (res) => {
            if (res?.status_code === CustomErrorCodes.ACCOUNT_NOT_SIGNED_UP) {
              this.createUser(segments);
            }
            this.subTorealtimeEvents(res);
            this.userService.emitUpdatedUser(res);
            // Update timezone if not already set, or is set to "auto"
            if (res.user && (!res.user?.timezone || res.user?.timezone?.autoDetect)) {
              this.userService.updateTimezone();
            }

            from(this.flagsService.getFlags())
              .pipe(
                // Flags are loaded now, so setupSocket() can read its config. Connect the WB
                // socket once for the app session (peer of the Galactus subs above), independent
                // of the spaces route so leaving a space never tears it down.
                tap(() => this.realtimeSpaceService.service.ensureConnected()),
                switchMap(() => {
                  // Only fetch courses if LMS capability is enabled
                  if (this.featureManagementService.isLmsEnabled()) {
                    return this.profilesService.getAllCourses();
                  } else {
                    return of(null);
                  }
                }),
              )
              .pipe(untilDestroyed(this))
              .subscribe((coursesRes) => {
                if (coursesRes) {
                  this.handleCoursesResolve(coursesRes);
                }
                if (route && segments) {
                  resolve(this.redirectOnLoad(route, segments));
                } else {
                  resolve(true);
                }
              });
          },
          error: (error) => {
            this.handleLoadTutorError(segments).then(() => {
              reject(error);
            });
          },
        });
      if (!this.inspectModeRepository.isInspectMode) {
        this.userService
          .getCustomAttributes('')
          .pipe(untilDestroyed(this))
          .subscribe((res) => {
            this.userService.allInstitutionCustomAttributes.next(res.custom_attributes);
          });
      }
    });
  }

  handleCoursesResolve(coursesRes) {
    if (coursesRes && coursesRes.courses) {
      this.profilesService.courses.next(coursesRes.courses);
    }

    if (!sessionStorage.getItem('modalShown')) {
      this.checkSupportedBrowser();
    }
  }

  async handleLoadTutorError(segments?: UrlSegment[]) {
    // if we fail to get user redirect to login page
    this.userService.appLoading.next(false);

    let continueUrl = segments?.join('/');
    if (continueUrl === this.LOGIN_PATH) {
      const queryParams = this.router.getCurrentNavigation()?.extractedUrl.queryParams;
      if (queryParams) {
        continueUrl = queryParams['continueUrl'];
      }

      AuthTracing.log('LoginGuard.handleLoadTutorError');
      await this.authService.signout();

      this.displayServerDownNotification();
    }

    if (segments) {
      this.router.navigate([`/${this.LOGIN_PATH}`], {
        queryParams: { continueUrl },
        skipLocationChange: false,
      });
    }
  }

  displayServerDownNotification(): void {
    this.notificationToasterService.showDefaultErrorNotificationV2(
      this.translate.instant('Error accessing Pencil Spaces'),
      this.translate.instant(
        'We’re facing some unexpected issues right now. Please try again later.',
      ),
      ERRORS.SERVER_DOWN,
    );
  }

  createUser(segments?: UrlSegment[]) {
    this.userService
      .createUser(this.firebaseUser?.providerId != 'password')
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          if (segments) {
            location.href = segments.join('/');
          }
        },
        error: () => {
          if (segments) {
            location.href = segments.join('/');
          }
        },
      });
  }

  subTorealtimeEvents(res: IUserInfo) {
    if (res && res.user && res.user._id) {
      this.realtimeService.subscribeNotification(
        res.user._id,
        res.realtime?.realtime_user_notification,
      );
      this.realtimeService.subscribeUserNotification(
        res.user._id,
        res.realtime?.realtime_user_event_notification,
      );
      if (this.flagsService.isFlagEnabled(FLAGS.ENABLE_REALTIME_RTM)) {
        this.realtimeService.subscribeSignalingRTM(
          res.user._id,
          res.realtime?.realtime_rtm_channel,
        );
      }
    }
  }

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    this.userService.appLoading.next(false);
    await this.userInfoInputDialogService.openDialogIfNeeded();
    const firebaseUser = this.authService.getCurrentUser();
    const appUser = this.userService.user.getValue();
    if (firebaseUser) {
      if (appUser && appUser.user.email !== firebaseUser.email) {
        return this.loadTutor();
      }

      return this.getAvailable();
    } else {
      if (
        !(
          state.url.includes('content/present') ||
          state.url.includes('content/view') ||
          state.url.includes('content/notes/view')
        )
      ) {
        if (state.url) {
          const urlTree = this.router.parseUrl(state.url);
          const urlParams = new URLSearchParams();
          Object.keys(urlTree.queryParams).forEach((key) => {
            urlParams.append(key, urlTree.queryParams[key]);
          });
          const segments = urlTree.root.children['primary']?.segments || [];
          return this.routeToAccount(urlParams, segments);
        } else {
          this.router.navigate(['/account'], {
            skipLocationChange: true,
          });
          return false;
        }
      }
      return this.getAvailable();
    }
  }

  async canLoad(route: Route, segments: UrlSegment[]): Promise<boolean> {
    try {
      await this.authService.loading;
    } catch (error) {
      captureException(error);
    }

    // Check if this is a single recording view route
    const isRecordingRoute =
      this.recordingDetailsService.isSingleRecordingViewRouteFromSegments(segments);
    if (isRecordingRoute) {
      // For unauthenticated users on recording routes, check if recording is public
      const recordingId = this.recordingDetailsService.extractRecordingIdFromSegments(segments);
      if (recordingId) {
        try {
          // Load recording details (will use cache if already loaded)
          const recordingState = await firstValueFrom(
            this.recordingDetailsService.loadRecordingDetails(recordingId),
          );

          // If recording is public, bypass login guard
          if (!this.firebaseUser && recordingState.isPublic && recordingState.recording) {
            return true;
          }
        } catch (error) {
          // If error loading recording, continue with normal flow
          captureException(error);
        }
      }
    }

    const isSessionRoute =
      this.analyticsSessionsDetailsService.isSingleSessionViewRouteFromSegments(segments);
    if (isSessionRoute) {
      const sessionId = this.analyticsSessionsDetailsService.extractSessionIdFromSegments(segments);
      if (sessionId) {
        try {
          // Uses cache if already loaded
          const sessionState = await firstValueFrom(
            this.analyticsSessionsDetailsService.loadSessionDetails(sessionId),
          );

          if (!this.firebaseUser && sessionState.isPublic && sessionState.session) {
            return true;
          }
        } catch (error) {
          captureException(error);
        }
      }
    }

    if (!this.user && this.firebaseUser) {
      return this.loadTutor(route, segments);
    } else {
      await this.flagsService.getFlags();

      return this.redirectOnLoad(route, segments);
    }
  }

  async redirectOnLoad(route: Route, segments: UrlSegment[]) {
    if (
      (segments[0] && segments[0].path === 'techcheck') ||
      (segments[0] &&
        segments[0].path === 'content' &&
        segments[1] &&
        (segments[1].path === 'view' ||
          segments[1].path === 'present' ||
          segments[1].path + segments[2]?.path === 'notesview'))
    ) {
      return this.getAvailable();
    }

    // If we have a user and url is calendar add-on, reirect to calendar auth page
    if (
      this.firebaseUser &&
      this.router.getCurrentNavigation()?.extractedUrl.queryParams['redirect'] === 'addOnLoginMode'
    ) {
      redirectToAppScript(this.firebaseUser.providerId, null, null, this.firebaseUser);
      return true;
    }
    // If we have a user, redirect away from signup/login pages
    if (
      this.user &&
      segments[0] &&
      segments[0].path === 'account' &&
      (!segments[1] || segments[1].path === 'login')
    ) {
      // If we are already redirecting, our pathname is empty.
      // We don't need to redirect if we're already going somewhere
      if (location.pathname !== '/') {
        if (this.accountEntryService.isOnlyPasswordProviderAndEmailUnverified(this.firebaseUser)) {
          this.accountEntryService.goToVerificationPage(this.firebaseUser?.email);
          return true;
        }

        return this.router.navigateByUrl(this.authService.getRedirectUrl());
      }
    }

    if (this.firebaseUser) {
      return this.getAvailable();
    } else {
      if (!route.path?.includes('account')) {
        const urlParams = new URLSearchParams(location.search);
        return this.routeToAccount(urlParams, segments);
      }
      return true;
    }
  }

  private extractGTMLinkerParams(urlParams: URLSearchParams): {
    linkerParams: Record<string, string>;
    remainingParams: URLSearchParams;
  } {
    const linkerParams: Record<string, string> = {};
    const remainingParams = new URLSearchParams();

    urlParams.forEach((value, key) => {
      // Preserve all GTM/GA parameters at top level:
      // - _gl: GTM cross-domain linker parameter
      // - _ga: Google Analytics client ID
      // - _ga_*: GA4 measurement-specific parameters
      // - _gcl_*: Google Click IDs (auto-tagging)
      // - _gac_*: Google Ads conversion tracking
      if (
        key.startsWith('_gl') ||
        key.startsWith('_ga') ||
        key.startsWith('_gcl_') ||
        key.startsWith('_gac_')
      ) {
        linkerParams[key] = value;
      } else {
        remainingParams.append(key, value);
      }
    });

    return { linkerParams, remainingParams };
  }

  public getContinueURL(urlParams: URLSearchParams, segments: UrlSegment[]) {
    if (urlParams.get('continueUrl')) {
      // Extract GTM params from existing continueUrl if present
      const { remainingParams } = this.extractGTMLinkerParams(urlParams);
      return remainingParams.toString();
    } else {
      const { remainingParams } = this.extractGTMLinkerParams(urlParams);
      const searchString = remainingParams.toString() ? `?${remainingParams.toString()}` : '';
      const fullPath = segments.join('/') + searchString;
      return `continueUrl=${encodeURIComponent(fullPath)}`;
    }
  }

  getAvailable(): boolean {
    if (
      this.isUploading &&
      confirm(
        this.translate.instant('Your file is still uploading. Are you sure you want to leave?'),
      )
    ) {
      return true;
    } else if (!this.isUploading) {
      return true;
    } else {
      return false;
    }
  }

  checkSupportedBrowser(): void {
    //  For now, we're just Hiding the modal without removing the code
    const shouldDisplayModal = false;
    if (!shouldDisplayModal) {
      return;
    }
    const isDesktopDevice = this.deviceService.isDesktop();
    const deviceBrowser = this.deviceService.getDeviceInfo().browser;
    if (isDesktopDevice && deviceBrowser.toLowerCase() !== 'chrome') {
      this.modalManagerService.showModal(
        NotSupportedBrowsersComponent,
        {
          width: '450px',
          panelClass: 'reset-fragment-dialog',
          height: '370px',
        },
        {
          afterClosed: (res) => {
            if (res) {
              window.open('https://www.google.com/chrome/');
            }
            sessionStorage.setItem('modalShown', 'true');
          },
        },
      );
    } else {
      sessionStorage.setItem('modalShown', 'true');
    }
  }

  public routeToAccount(urlParams: URLSearchParams, segments: UrlSegment[]) {
    const { linkerParams, remainingParams } = this.extractGTMLinkerParams(urlParams);
    const continueUrl = this.getContinueURL(remainingParams, segments);

    // Build query params with GTM linker params at top level
    const queryParams: any = {};

    // Add GTM linker parameters first
    Object.keys(linkerParams).forEach((key) => {
      queryParams[key] = linkerParams[key];
    });

    // Parse continueUrl to add it as well
    const continueUrlParts = continueUrl.split('=');
    if (continueUrlParts[0] === 'continueUrl' && continueUrlParts[1]) {
      queryParams['continueUrl'] = decodeURIComponent(continueUrlParts[1]);
    } else {
      // Handle case where continueUrl already contains other params
      const params = new URLSearchParams(continueUrl);
      params.forEach((value, key) => {
        queryParams[key] = key === 'continueUrl' ? decodeURIComponent(value) : value;
      });
    }

    const rerouteURL = `/account?${new URLSearchParams(queryParams).toString()}`;
    return this.router.navigateByUrl(rerouteURL, { skipLocationChange: false });
  }
}
